// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * This module provides a component for displaying the audit history of a location.
 * It fetches and renders a chronological list of audit events, showing changes
 * and activities that have occurred at the location.
 */
import { getSiteAuditEvents } from '@/audit/api/get-location-audit-events';
import { AuditEventListItem } from '@/audit/components/audit-event-list-item';
import { Stack } from '@mui/material';
import { useSuspenseQuery } from '@tanstack/react-query';

interface Props {
  /**
   * The unique identifier for the location/site.
   * Used to fetch audit events specific to this location.
   */
  siteKey: string;
}

/**
 * A component that displays a chronological list of audit events for a location.
 * Uses React Suspense for loading states and error boundaries for error handling.
 *
 * Features:
 * - Automatic data fetching with React Query
 * - Real-time updates through query invalidation
 * - Vertical stacking of audit events
 * - Consistent spacing between events
 *
 * @example
 * ```tsx
 * <SuspenseErrorBoundary>
 *   <Suspense fallback={<LoadingSpinner />}>
 *     <SiteAuditList siteKey="loc_123" />
 *   </Suspense>
 * </SuspenseErrorBoundary>
 * ```
 */
export function SiteAuditList(props: Props) {
  // Fetch audit events with automatic caching and revalidation
  const { data } = useSuspenseQuery({
    queryKey: ['location', props.siteKey, 'audit'],
    queryFn: () => getSiteAuditEvents(props.siteKey),
    retry: false,
  });

  return (
    <Stack spacing={2}>
      {data.events.map((event) => (
        <AuditEventListItem key={event.id} event={event} />
      ))}
    </Stack>
  );
}
